import type { ChartSort } from "@/shared/types";
import { dayStartMs } from "@/widgets/machine/shared";

/**
 * Chart sort helpers (pure, convention section 24): input → function →
 * output. Sorting happens on the DOMAIN ROWS before the dataset is built —
 * the library's `EChart` stays business-free and renders the order it is
 * given. Client-side widgets apply `applyChartSort` in their `logic.ts`;
 * server-side widgets (paginated datasets) turn the sort into a fetch
 * param via `sortToCubeOrder`.
 */

/**
 * Sorts the rows by the selected field. Every widget declares a concrete
 * default sort (e.g. the date ascending) — there is no "no sort" state.
 * Comparison: numbers compare numerically, strings fall back to
 * `localeCompare`; a widget can override per field (e.g. the `dd/MM/yyyy`
 * day-label comparator) via `compares`. Stable for ties (the original array
 * order is the tie-breaker — `Array.prototype.sort` is guaranteed stable).
 */
export function applyChartSort<T>(
  points: T[],
  sort: ChartSort,
  compares: Partial<Record<string, (a: T, b: T) => number>> = {},
): T[] {
  const { id, desc } = sort;
  const custom = compares[id];
  const sign = desc ? -1 : 1;
  // `[...points].sort` — a stable copy sort (`toSorted` needs lib es2023).
  return [...points].sort((a: T, b: T) => {
    const customResult = custom?.(a, b);
    if (customResult !== undefined && customResult !== 0) {
      return sign * customResult;
    }
    if (customResult !== undefined) {
      return 0;
    }
    const valueA = (a as Record<string, unknown>)[id];
    const valueB = (b as Record<string, unknown>)[id];
    const base =
      typeof valueA === "number" && typeof valueB === "number"
        ? valueA - valueB
        : String(valueA).localeCompare(String(valueB));
    return sign * base;
  });
}

/** `dd/MM/yyyy` day-label comparator (chronological — never string order). */
export function compareByDayLabel<T>(
  read: (point: T) => string,
): (a: T, b: T) => number {
  return (a, b) => dayStartMs(read(a)) - dayStartMs(read(b));
}

/**
 * Server-side sort (FR-A5): turns the selected sort into the CubeJS
 * `order` object. `fieldMap` translates the widget's data field into the
 * Cube measure/dimension name (e.g. `oee → Oee.oee`); unmapped fields pass
 * through unchanged.
 */
export function sortToCubeOrder(
  sort: ChartSort,
  fieldMap: Record<string, string> = {},
): Record<string, "asc" | "desc"> {
  const field = fieldMap[sort.id] ?? sort.id;
  return { [field]: sort.desc ? "desc" : "asc" };
}
